/* ============================================================
   ViewToolbar — 데이터베이스 툴바의 필터 / 정렬 / 검색 컨트롤
   - FilterControl: 컬럼별 조건 팝오버 (다중 필터 AND, 활성 개수 뱃지)
   - SortControl:   컬럼 + 오름/내림차순, 다중 정렬(우선순위 = 배열 순서)
   - SearchControl: 클릭 시 인라인 입력 확장, 텍스트성 속성 부분일치
   필터/정렬 배열의 persist(db_views.config)는 DatabasePage 가 담당한다.
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { OPS_BY_TYPE, opsFor, needsValue, isFilterComplete } from './viewPipeline';

/* 외부 클릭 감지 (DatabasePage 의 useOutsideClose 와 동일 패턴) */
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

const colOf = (columns, key) => columns.find((c) => c.key === key);

/* ---------------- 필터 값 입력기 (타입별) ---------------- */
function FilterValueEditor({ col, filter, onChange }) {
  if (!needsValue(filter.op)) return null;

  if (col.type === 'select') {
    return (
      <select
        className="fp-value"
        value={filter.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      >
        <option value="">값 선택…</option>
        {(col.options || []).map((o) => (
          <option key={o.name} value={o.name}>{o.name}</option>
        ))}
      </select>
    );
  }
  if (col.type === 'number') {
    return (
      <input
        className="fp-value"
        type="number"
        value={filter.value ?? ''}
        placeholder="숫자"
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }
  if (col.type === 'date') {
    return (
      <input
        className="fp-value"
        type="date"
        value={filter.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }
  // text / title / url / person / relation — 포함 검색
  return (
    <input
      className="fp-value"
      value={filter.value ?? ''}
      placeholder="값 포함…"
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
}

/* ---------------- ⚙ 필터 ---------------- */
export function FilterControl({ columns, filters, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const filterable = columns.filter((c) => !!OPS_BY_TYPE[c.type]);
  const activeCount = (filters || []).filter(isFilterComplete).length;

  const update = (i, patch) =>
    onChange(filters.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i) => onChange(filters.filter((_, idx) => idx !== i));
  const add = () => {
    const col = filterable[0];
    if (!col) return;
    onChange([...(filters || []), { id: `f_${Date.now()}`, column: col.key, op: opsFor(col)[0].op, value: '' }]);
  };
  const changeColumn = (i, key) => {
    const col = colOf(columns, key);
    update(i, { column: key, op: opsFor(col)[0].op, value: '' });
  };
  const changeOp = (i, op) => update(i, { op, ...(needsValue(op) ? {} : { value: '' }) });

  return (
    <span className="tool-wrap" ref={ref}>
      <button
        className={`t${activeCount ? ' t-on' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="필터"
      >
        ⚙ 필터{activeCount > 0 && <span className="t-badge">{activeCount}</span>}
      </button>
      {open && (
        <div className="tool-pop">
          <div className="tp-title">
            필터 <span className="tp-sub">모든 조건 만족(AND)</span>
          </div>
          {(filters || []).length === 0 && (
            <div className="tp-empty">조건이 없습니다. 아래에서 추가하세요.</div>
          )}
          {(filters || []).map((f, i) => {
            const col = colOf(columns, f.column);
            if (!col) {
              // 삭제된 컬럼을 참조하는 조건 — 제거만 가능하게 노출
              return (
                <div className="tp-row" key={f.id || i}>
                  <span className="tp-orphan">(삭제된 속성)</span>
                  <button className="tp-del" onClick={() => remove(i)} title="삭제">×</button>
                </div>
              );
            }
            const ops = opsFor(col);
            return (
              <div className="tp-row" key={f.id || i}>
                <select
                  className="fp-col"
                  value={f.column}
                  onChange={(e) => changeColumn(i, e.target.value)}
                >
                  {filterable.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
                <select
                  className="fp-op"
                  value={f.op}
                  onChange={(e) => changeOp(i, e.target.value)}
                >
                  {ops.map((o) => (
                    <option key={o.op} value={o.op}>{o.label}</option>
                  ))}
                </select>
                <FilterValueEditor col={col} filter={f} onChange={(patch) => update(i, patch)} />
                <button className="tp-del" onClick={() => remove(i)} title="삭제">×</button>
              </div>
            );
          })}
          <button className="tp-add" onClick={add} disabled={!filterable.length}>
            ＋ 필터 추가
          </button>
        </div>
      )}
    </span>
  );
}

/* ---------------- ↕ 정렬 ---------------- */
export function SortControl({ columns, sorts, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const activeCount = (sorts || []).filter((s) => s && s.column && colOf(columns, s.column)).length;

  const update = (i, patch) =>
    onChange(sorts.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(sorts.filter((_, idx) => idx !== i));
  const add = () => {
    const col = columns[0];
    if (!col) return;
    onChange([...(sorts || []), { column: col.key, dir: 'asc' }]);
  };

  return (
    <span className="tool-wrap" ref={ref}>
      <button
        className={`t${activeCount ? ' t-on' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="정렬"
      >
        ↕ 정렬{activeCount > 0 && <span className="t-badge">{activeCount}</span>}
      </button>
      {open && (
        <div className="tool-pop">
          <div className="tp-title">
            정렬 <span className="tp-sub">위에서부터 우선 적용</span>
          </div>
          {(sorts || []).length === 0 && (
            <div className="tp-empty">정렬 기준이 없습니다. 아래에서 추가하세요.</div>
          )}
          {(sorts || []).map((s, i) => {
            const col = colOf(columns, s.column);
            if (!col) {
              return (
                <div className="tp-row" key={i}>
                  <span className="tp-orphan">(삭제된 속성)</span>
                  <button className="tp-del" onClick={() => remove(i)} title="삭제">×</button>
                </div>
              );
            }
            return (
              <div className="tp-row" key={i}>
                <select
                  className="fp-col"
                  value={s.column}
                  onChange={(e) => update(i, { column: e.target.value })}
                >
                  {columns.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
                <span className="tp-dir">
                  <button
                    className={s.dir !== 'desc' ? 'on' : ''}
                    onClick={() => update(i, { dir: 'asc' })}
                  >
                    오름차순
                  </button>
                  <button
                    className={s.dir === 'desc' ? 'on' : ''}
                    onClick={() => update(i, { dir: 'desc' })}
                  >
                    내림차순
                  </button>
                </span>
                <button className="tp-del" onClick={() => remove(i)} title="삭제">×</button>
              </div>
            );
          })}
          <button className="tp-add" onClick={add} disabled={!columns.length}>
            ＋ 정렬 추가
          </button>
        </div>
      )}
    </span>
  );
}

/* ---------------- 🔍 검색 (인라인 확장) ---------------- */
export function SearchControl({ value, onChange }) {
  const [open, setOpen] = useState(!!value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) {
    return (
      <button className="t" onClick={() => setOpen(true)} title="검색">🔍</button>
    );
  }

  return (
    <span className="db-search">
      <span aria-hidden="true">🔍</span>
      <input
        ref={inputRef}
        value={value}
        placeholder="검색…"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onChange('');
            setOpen(false);
          }
        }}
        onBlur={() => { if (!value) setOpen(false); }}
      />
      {value && (
        <button
          className="db-search-x"
          title="지우기"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(''); inputRef.current?.focus(); }}
        >
          ×
        </button>
      )}
    </span>
  );
}
